import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Save, Printer, Mail, Pencil, History, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/pages/cauzioni/cauApi";
import CauShell from "@/pages/cauzioni/CauShell";
import { EsitoBadge } from "@/pages/cauzioni/Badges";
import { euro, TIPI_GARANZIA } from "@/pages/cauzioni/labels";
import { LOGO_URL, AGENCY } from "@/pages/cauzioni/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

function EmailDialog({ praticaId, ragione }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ recipient_email: "", subject: `Relazione tecnica assuntiva — ${ragione}`, note: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!f.recipient_email) { toast.error("Inserisci l'email destinatario"); return; }
    setBusy(true);
    try { await api.post(`/pratiche/${praticaId}/email`, f); toast.success("Email inviata"); setOpen(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="rounded-sm" data-testid="email-open-button"><Mail size={15} className="mr-1.5" /> Invia alla compagnia</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Invia relazione via email</DialogTitle>
          <DialogDescription>Invio disponibile dopo la configurazione del provider email (Resend).</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Email destinatario</label><Input type="email" value={f.recipient_email} onChange={(e) => setF({ ...f, recipient_email: e.target.value })} data-testid="email-recipient" /></div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Oggetto</label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="email-subject" /></div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Nota introduttiva</label><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} rows={3} data-testid="email-note" /></div>
        </div>
        <DialogFooter><Button onClick={send} disabled={busy} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="email-send-button">{busy ? "Invio…" : "Invia email"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`font-medium ${highlight ? "text-amber-700" : "text-slate-800"}`}>{value}</div>
    </div>
  );
}

export default function CauRelazione() {
  const { id } = useParams();
  const nav = useNavigate();
  const [pratica, setPratica] = useState(null);
  const [audit, setAudit] = useState([]);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.get(`/pratiche/${id}`), api.get(`/pratiche/${id}/audit`)])
    .then(([p, a]) => { setPratica(p.data); setText(p.data.relazioneTesto || ""); setAudit(a.data); });
  useEffect(() => { load().catch(() => { toast.error("Errore"); nav("/cauzioni-pro"); }); /* eslint-disable-next-line */ }, [id]);

  const generate = async () => {
    if (!pratica?.parere?.giudizio) {
      toast.error("Seleziona prima il parere dell'intermediario (step Parere dell'istruttoria) per generare la relazione.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/pratiche/${id}/relazione/generate`);
      toast.info("Generazione avviata: l'AI sta analizzando i documenti, può richiedere 1-2 minuti…");
      // polling stato (evita il timeout del gateway in produzione)
      const started = Date.now();
      const poll = async () => {
        try {
          const { data } = await api.get(`/pratiche/${id}/relazione/status`);
          if (data.status === "done") {
            setText(data.relazioneTesto || "");
            await load();
            toast.success("Relazione generata dall'AI");
            setBusy(false);
            return;
          }
          if (data.status === "error") {
            toast.error(data.error || "Errore generazione relazione");
            setBusy(false);
            return;
          }
          if (Date.now() - started > 5 * 60 * 1000) {
            toast.error("La generazione sta impiegando troppo tempo. Riprova più tardi.");
            setBusy(false);
            return;
          }
          setTimeout(poll, 4000);
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
          setBusy(false);
        }
      };
      setTimeout(poll, 4000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };
  const saveText = async () => {
    setBusy(true);
    try { await api.put(`/pratiche/${id}/relazione`, { relazioneTesto: text }); await load(); setEditing(false); toast.success("Relazione salvata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/pratiche/${id}/relazione/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Relazione_${(cl.ragioneSociale || "cliente").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Genera prima la relazione"); }
  };

  if (!pratica) return <CauShell><div className="p-16 text-center text-slate-400">Caricamento…</div></CauShell>;
  const cl = pratica.cliente || {};

  return (
    <CauShell>
      <div className="mx-auto max-w-[1400px] px-6 py-6" data-testid="cau-relazione-page">
        <div className="no-print flex items-center justify-between mb-5">
          <button onClick={() => nav(`/cauzioni-pro/pratica/${id}`)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-wizard"><ArrowLeft size={15} /> Torna all'istruttoria</button>
          <div className="flex flex-wrap gap-2">
            <Button onClick={generate} disabled={busy || !pratica.parere?.giudizio} title={!pratica.parere?.giudizio ? "Seleziona il parere dell'intermediario nell'istruttoria" : undefined} variant="outline" className="rounded-sm" data-testid="generate-relazione-button"><Sparkles size={15} className="mr-1.5" /> {busy ? "Generazione…" : (pratica.relazioneTesto ? "Rigenera AI" : "Genera con AI")}</Button>
            {editing ? (
              <Button onClick={saveText} disabled={busy} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-relazione-button"><Save size={15} className="mr-1.5" /> Salva</Button>
            ) : (
              <Button onClick={() => setEditing(true)} variant="outline" className="rounded-sm" data-testid="edit-relazione-button"><Pencil size={15} className="mr-1.5" /> Modifica</Button>
            )}
            <Button onClick={() => window.print()} variant="outline" className="rounded-sm" data-testid="print-button"><Printer size={15} className="mr-1.5" /> Stampa</Button>
            <Button onClick={downloadPdf} variant="outline" className="rounded-sm" data-testid="download-pdf-button"><FileDown size={15} className="mr-1.5" /> Scarica PDF</Button>
            <EmailDialog praticaId={id} ragione={cl.ragioneSociale} />
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_300px] gap-6">
          <div className="bg-white border border-slate-200 shadow-md rounded-sm mx-auto w-full max-w-4xl p-12 print-area" data-testid="relazione-document">
            <div className="flex items-center justify-between gap-4 pb-5 mb-5 border-b-2 border-slate-900">
              <img src={LOGO_URL} alt="Assietica" className="h-16 w-auto object-contain" />
              <div className="text-right">
                <div className="text-[11px] text-slate-500">{AGENCY.tagline}</div>
                <div className="text-[11px] text-slate-400">RUI {AGENCY.rui} · {AGENCY.email}</div>
              </div>
            </div>
            <div className="flex items-start justify-between border-b border-slate-200 pb-6 mb-6">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400">Relazione tecnica assuntiva</div>
                <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">{cl.ragioneSociale || "—"}</h1>
                <div className="text-sm text-slate-500 mt-1">{cl.formaGiuridica} · P.IVA {cl.partitaIva || "—"} · CF {cl.codiceFiscale || "—"}</div>
              </div>
              <div className="text-right">
                <EsitoBadge esito={pratica.esitoFinale} className="text-sm px-3 py-1" />
                <div className="font-display text-2xl font-bold text-slate-900 mt-2">{pratica.scoreTotale != null ? `${pratica.scoreTotale}/100` : "—"}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm mb-8">
              <Info label="Tipo garanzia" value={TIPI_GARANZIA[pratica.tipoGaranzia]} />
              <Info label="Ente beneficiario" value={pratica.enteBeneficiario || "—"} />
              <Info label="Importo" value={euro(pratica.importo)} />
              <Info label="Durata" value={`${pratica.durataMesi || 0} mesi`} />
              <Info label="Premio annuale" value={euro(pratica.premioAnnuale)} />
              <Info label="Premio totale" value={euro(pratica.premioTotale)} highlight={pratica.durataMesi > 12} />
            </div>
            {editing ? (
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={24} className="font-mono text-sm" data-testid="relazione-textarea" />
            ) : busy ? (
              <div className="text-center py-16 border border-dashed border-slate-200 rounded-sm bg-slate-50/60" data-testid="relazione-generating">
                <Loader2 size={34} className="mx-auto text-slate-700 animate-spin" />
                <div className="mt-4 font-display text-lg font-semibold text-slate-800">Analisi documenti in corso…</div>
                <div className="mt-1.5 text-sm text-slate-500 max-w-md mx-auto">
                  L'AI sta leggendo bilanci, visure e allegati per redigere la relazione tecnica. L'operazione può richiedere 1-2 minuti: puoi restare su questa pagina.
                </div>
                <div className="mt-4 flex justify-center gap-1.5" aria-hidden>
                  <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            ) : text ? (
              <div className="prose prose-slate max-w-none whitespace-pre-wrap text-[15px] leading-relaxed text-slate-800" data-testid="relazione-text">{text}</div>
            ) : (
              <div className="text-center py-16 text-slate-400 border border-dashed border-slate-200 rounded-sm">
                Nessuna relazione generata. Usa "Genera con AI" per creare una bozza dai dati istruttori, oppure "Modifica" per redigerla manualmente.
              </div>
            )}
            <div className="mt-10 pt-6 border-t border-slate-200">
              <div className="font-display font-bold text-slate-900">{AGENCY.name}</div>
              <div className="text-xs text-slate-500 mb-3">{AGENCY.tagline}</div>
              <div className="grid grid-cols-3 gap-3 text-[11px] text-slate-600">
                {AGENCY.sedi.map((s) => (
                  <div key={s.city}>
                    <div className="font-semibold text-slate-800">Sede di {s.city}</div>
                    <div>{s.addr}</div>
                    {s.tel && <div>Tel {s.tel}</div>}
                    {s.cell && <div>Cell {s.cell}</div>}
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 mt-3">Mail {AGENCY.email} · PEC {AGENCY.pec} · RUI {AGENCY.rui}</div>
            </div>
          </div>

          <aside className="no-print">
            <div className="bg-white border border-slate-200 rounded-sm p-5 lg:sticky lg:top-6">
              <div className="flex items-center gap-2 text-slate-700 font-medium mb-4"><History size={16} /> Audit trail</div>
              <ol className="space-y-3" data-testid="audit-list">
                {audit.length === 0 && <li className="text-sm text-slate-400">Nessuna attività.</li>}
                {audit.map((a) => (
                  <li key={a.id} className="border-l-2 border-slate-200 pl-3">
                    <div className="text-sm font-medium text-slate-800">{a.action.replace(/_/g, " ")}</div>
                    {a.detail && <div className="text-xs text-slate-500">{a.detail}</div>}
                    <div className="text-[11px] text-slate-400">{a.user_name} · {new Date(a.timestamp).toLocaleString("it-IT")}</div>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </CauShell>
  );
}
